import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { getAuthenticatedAvatarUrl } from 'Shared/Utils/Domain/Services/AvatarUrlService';

const ITEMS_PER_PAGE = 3;

const HomeLatestHiresSection = ({ hires = [] }) => {
  const displayHires = hires || [];
  const totalPages = Math.ceil(displayHires.length / ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [displayHires.length]);

  useEffect(() => {
    if (totalPages <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalPages]);

  const handleNextPage = () => {
    if (totalPages > 1) {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }
  };

  const visibleHires = displayHires.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        bgcolor: '#ffffff',
        p: { xs: 2, sm: 2.5, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: '#1f7bb6',
            fontSize: '1.05rem',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          ÚLTIMAS INCORPORACIONES
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          flex: 1,
          py: 0.5,
        }}
      >
        {visibleHires.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>
              No se han registrado nuevas incorporaciones recientemente.
            </Typography>
          </Box>
        ) : (
          visibleHires.map((hire) => {
            const authAvatarUrl = hire.avatar ? getAuthenticatedAvatarUrl(hire.avatar) : null;

            return (
              <Box
                key={hire.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateX(4px)' },
                }}
              >
                <Avatar
                  src={authAvatarUrl || undefined}
                  alt={hire.fullName}
                  imgProps={{ style: { objectFit: 'cover' } }}
                  sx={{
                    width: { xs: 72, sm: 82, md: 84 },
                    height: { xs: 72, sm: 82, md: 84 },
                    flexShrink: 0,
                    bgcolor: '#257eb9',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.8rem',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    border: '3px solid #f1f5f9',
                  }}
                >
                  {hire.fullName ? hire.fullName.charAt(0).toUpperCase() : 'U'}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: '#1f7bb6',
                      fontSize: { xs: '0.9rem', sm: '0.98rem' },
                      fontWeight: 800,
                      lineHeight: 1.25,
                      textTransform: 'uppercase',
                      letterSpacing: 0.2,
                    }}
                  >
                    {hire.fullName}
                  </Typography>

                  {hire.jobPosition && (
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                        fontWeight: 500,
                        mt: 0.5,
                        lineHeight: 1.3,
                      }}
                    >
                      {hire.jobPosition}
                    </Typography>
                  )}

                  {hire.delegation && (
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: { xs: '0.78rem', sm: '0.82rem' },
                        fontWeight: 400,
                        mt: 0.2,
                        lineHeight: 1.3,
                      }}
                    >
                      {hire.delegation}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Carousel Controls (only if more than 3 hires) */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1.5, mt: 'auto', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            {Array.from({ length: totalPages }).map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentPage(index)}
                sx={{
                  width: index === currentPage ? 20 : 7,
                  height: 7,
                  borderRadius: '4px',
                  bgcolor: index === currentPage ? '#257eb9' : '#cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { bgcolor: index === currentPage ? '#257eb9' : '#94a3b8' },
                }}
              />
            ))}
          </Box>
          <IconButton
            onClick={handleNextPage}
            size="small"
            aria-label="Ver más incorporaciones"
            sx={{
              color: '#1f7bb6',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(2px)',
                bgcolor: 'rgba(31, 123, 182, 0.08)',
              },
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

HomeLatestHiresSection.propTypes = {
  hires: PropTypes.array,
};

export default HomeLatestHiresSection;

